import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, FolderPlus, Save, Image as ImageIcon, CheckSquare, Square } from "lucide-react";

const NEW = "__new__";

export const SaveImageToCatalog = ({ src, srcs, open, onClose }) => {
  const list = srcs && srcs.length ? srcs : src ? [src] : [];
  const multi = list.length > 1;
  const qc = useQueryClient();
  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data, enabled: open });
  const sections = cats.filter((c) => !c.parent_id);
  const [title, setTitle] = useState("");
  const [section, setSection] = useState("");
  const [sub, setSub] = useState("");
  const [newName, setNewName] = useState("");
  const [price, setPrice] = useState(4);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setTitle(""); setSection(""); setSub(""); setNewName(""); } }, [open, src]);
  const subs = cats.filter((c) => c.parent_id === section);

  const save = async () => {
    if (!title.trim()) { toast.error("Give the design a title"); return; }
    setSaving(true);
    try {
      let section_id = section || null, subsection_id = sub || null;
      if (section === NEW) {
        if (!newName.trim()) { toast.error("Name the new folder"); setSaving(false); return; }
        const { data } = await api.post("/admin/categories", { name: newName.trim(), parent_id: null });
        section_id = data.category_id; subsection_id = null;
      } else if (sub === NEW) {
        if (!newName.trim()) { toast.error("Name the new sub-folder"); setSaving(false); return; }
        const { data } = await api.post("/admin/categories", { name: newName.trim(), parent_id: section });
        subsection_id = data.category_id;
      }
      let ok = 0, dup = 0;
      for (let i = 0; i < list.length; i++) {
        const t = multi ? `${title.trim()} ${i + 1}` : title.trim();
        try { await api.post("/designs", { title: t, image_url: list[i], base_price: Number(price) || 4, section_id, subsection_id, tags: [] }); ok++; }
        catch (e) { if (e.response?.status === 409) dup++; else throw e; }
      }
      if (multi) toast.success(`${ok} image${ok === 1 ? "" : "s"} saved to the catalog${dup ? ` · ${dup} already there` : ""}`);
      else if (dup) { toast.error("Duplicate: this image is already in the catalog"); setSaving(false); return; }
      else toast.success(`"${title.trim()}" saved to the catalog`);
      qc.invalidateQueries({ queryKey: ["designs"] });
      qc.invalidateQueries({ queryKey: ["categories"] });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save design");
    } finally { setSaving(false); }
  };

  const selCls = "w-full h-9 rounded-md bg-[#1e222d] border border-zinc-700 text-white text-sm px-2";
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#12141a] border-zinc-800 text-zinc-100 max-w-md" data-testid="save-image-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ImageIcon size={18} className="text-[#22c3d6]" />{multi ? `Save ${list.length} images to catalog` : "Save image to catalog"}</DialogTitle>
          <DialogDescription className="text-sm text-zinc-400">{multi ? "All selected images go into one folder; titles are numbered from the title below." : "Pick a folder (or create one) and this image becomes a reusable catalog design."}</DialogDescription>
        </DialogHeader>
        {multi && <div className="flex flex-wrap gap-1.5" data-testid="save-image-multi-preview">{list.slice(0, 12).map((u) => <span key={u} className="h-12 w-12 checker rounded overflow-hidden flex items-center justify-center border border-zinc-800"><img src={fileUrl(u)} alt="" className="max-w-full max-h-full object-contain" /></span>)}{list.length > 12 && <span className="text-xs text-zinc-500 self-center">+{list.length - 12}</span>}</div>}
        {!multi && src && (
          <div className="flex items-center gap-3">
            <span className="h-20 w-20 checker rounded-lg overflow-hidden flex items-center justify-center shrink-0 border border-zinc-800"><img src={fileUrl(src)} alt="" className="max-w-full max-h-full object-contain" data-testid="save-image-preview" /></span>
            <a href={fileUrl(src)} download target="_blank" rel="noreferrer" data-testid="download-original-link" className="text-xs text-[#ffd600] hover:text-white inline-flex items-center gap-1 font-mono-p"><Download size={13} />Download original</a>
          </div>
        )}
        <div className="space-y-3">
          <div><label className="text-xs text-zinc-400 font-mono-p">Title</label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={multi ? "e.g. Order 1042 art (→ 'Order 1042 art 1', '… 2')" : "e.g. Skull Rose"} data-testid="save-image-title" className="bg-[#1e222d] border-zinc-700 text-white mt-1" /></div>
          <div>
            <label className="text-xs text-zinc-400 font-mono-p">Folder</label>
            <select value={section} onChange={(e) => { setSection(e.target.value); setSub(""); setNewName(""); }} data-testid="save-image-section" className={`${selCls} mt-1`}>
              <option value="">No folder (root)</option>
              {sections.map((s) => <option key={s.category_id} value={s.category_id}>{s.name}</option>)}
              <option value={NEW}>+ New folder…</option>
            </select>
          </div>
          {section && section !== NEW && (
            <div>
              <label className="text-xs text-zinc-400 font-mono-p">Sub-folder <span className="text-zinc-600">(optional)</span></label>
              <select value={sub} onChange={(e) => { setSub(e.target.value); setNewName(""); }} data-testid="save-image-subsection" className={`${selCls} mt-1`}>
                <option value="">None</option>
                {subs.map((s) => <option key={s.category_id} value={s.category_id}>{s.name}</option>)}
                <option value={NEW}>+ New sub-folder…</option>
              </select>
            </div>
          )}
          {(section === NEW || sub === NEW) && (
            <div className="flex items-center gap-2"><FolderPlus size={16} className="text-[#10b981] shrink-0" /><Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={section === NEW ? "New folder name" : "New sub-folder name"} data-testid="save-image-new-folder" className="bg-[#1e222d] border-zinc-700 text-white" /></div>
          )}
          <div><label className="text-xs text-zinc-400 font-mono-p">Base price ($)</label><Input type="number" min={0} step={0.5} value={price} onChange={(e) => setPrice(e.target.value)} data-testid="save-image-price" className="bg-[#1e222d] border-zinc-700 text-white mt-1 w-32" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="border-zinc-700 bg-transparent text-white hover:bg-zinc-800 rounded-full" data-testid="save-image-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#22c3d6] hover:bg-[#1ba9ba] text-black rounded-full font-semibold" data-testid="save-image-submit"><Save size={14} className="mr-1" />{saving ? "Saving…" : multi ? `Save ${list.length} to catalog` : "Save to catalog"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SheetImagesDialog = ({ srcs, open, onClose, title }) => {
  const [saveSrc, setSaveSrc] = useState(null);
  const [bulk, setBulk] = useState(false);
  const [sel, setSel] = useState(new Set());
  const unique = [...new Set(srcs || [])];
  useEffect(() => { if (open) { setSel(new Set()); setBulk(false); } }, [open]);
  const toggle = (u) => setSel((p) => { const n = new Set(p); n.has(u) ? n.delete(u) : n.add(u); return n; });
  const allSel = sel.size === unique.length && unique.length > 0;
  return (
    <>
      <Dialog open={open && !saveSrc && !bulk} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="bg-[#12141a] border-zinc-800 text-zinc-100 max-w-lg" data-testid="sheet-images-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Images on {title || "this gang sheet"}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">{unique.length} unique image{unique.length === 1 ? "" : "s"} — save any to your catalog or download the original.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <button onClick={() => setSel(allSel ? new Set() : new Set(unique))} data-testid="sheet-images-select-all" className="text-xs text-zinc-300 hover:text-white font-mono-p inline-flex items-center gap-1 border border-zinc-700 rounded-full px-2 py-0.5 hover:border-white">{allSel ? <CheckSquare size={13} /> : <Square size={13} />}{allSel ? "Deselect all" : "Select all"}</button>
            <Button size="sm" disabled={sel.size === 0} onClick={() => setBulk(true)} data-testid="sheet-images-bulk-save" className="bg-[#10b981] hover:bg-[#0e9e70] disabled:opacity-50 text-white rounded-full h-8"><Save size={13} className="mr-1" />Save {sel.size || ""} selected to one folder</Button>
          </div>
          <div className="max-h-[60vh] overflow-auto space-y-2">
            {unique.map((s, i) => (
              <div key={s} className={`flex items-center gap-3 bg-[#1e222d] border rounded-lg p-2 ${sel.has(s) ? "border-[#10b981]/60" : "border-zinc-800"}`} data-testid={`sheet-image-row-${i}`}>
                <button onClick={() => toggle(s)} data-testid={`sheet-image-check-${i}`} className="shrink-0 text-zinc-400 hover:text-white">{sel.has(s) ? <CheckSquare size={16} className="text-[#10b981]" /> : <Square size={16} />}</button>
                <span className="h-14 w-14 checker rounded overflow-hidden flex items-center justify-center shrink-0"><img src={fileUrl(s)} alt="" className="max-w-full max-h-full object-contain" /></span>
                <span className="flex-1 text-xs text-zinc-400 font-mono-p truncate">{s.split("/").pop()}</span>
                <a href={fileUrl(s)} download target="_blank" rel="noreferrer" title="Download original" data-testid={`sheet-image-download-${i}`} className="w-8 h-8 rounded flex items-center justify-center text-[#ffd600] hover:bg-zinc-800"><Download size={15} /></a>
                <Button size="sm" onClick={() => setSaveSrc(s)} data-testid={`sheet-image-save-${i}`} className="bg-[#22c3d6]/20 text-[#22c3d6] border border-[#22c3d6]/40 hover:bg-[#22c3d6]/30 rounded-full h-8"><Save size={13} className="mr-1" />Save</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <SaveImageToCatalog src={saveSrc} open={!!saveSrc} onClose={() => setSaveSrc(null)} />
      <SaveImageToCatalog srcs={[...sel]} open={bulk} onClose={() => { setBulk(false); setSel(new Set()); }} />
    </>
  );
};
